import { memo, useEffect, useRef, useState } from "react";
import Container from "../../../components/Container";
import { useAuth } from "../../../hooks/useAuth";
import { ILivechatMessage, IUser } from "sierra";
import api from "../../../utils/axios";
import socket from "../../../utils/socket";
import { Avatar, Badge, CircularProgress, Divider, Grid2, IconButton, Skeleton, TextField, Typography } from "@mui/material";
import { Send } from "@mui/icons-material";
import LiveChatSkeleton from "../../../components/Skeletons/LiveChatSkeleton";
import { useApp } from "../../../contexts/AppContext";

interface ICustomerWithMessages extends IUser {
    messages: ILivechatMessage[]
}

export default function SystemLiveChat() {
    const { user, userLoaded } = useAuth()
    const { lang } = useApp()

    const [message, setMessage] = useState('');
    const [customers, setCustomers] = useState<IUser[]>();
    const [messages, setMessages] = useState<ILivechatMessage[]>();
    const [customersMessages, setCustomersMessages] = useState<ICustomerWithMessages[]>()
    const [apiCalling, setApiCalling] = useState(false);
    const [index, setIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleClick = (index: number) => {
        const customer = customersMessages?.[index]
        if (!customer) return;
        setIndex(index)
        markAsRead()
        scrollToBottom()
    }

    useEffect(() => {
        // Scroll to the bottom when messages change
        markAsRead()
        scrollToBottom()
    }, [messages]);

    useEffect(() => {
        if (!userLoaded) return
        if (user) {
            fetchMessages()
            fetchCustomers()
            socket.on("live_chat_messages_changed", fetchMessages);
            return () => {
                socket.off("live_chat_messages_changed", fetchMessages);
            }
        }
    }, [userLoaded])

    useEffect(() => {
        if (!messages || !customers) return;
        setCustomersMessages(customers.map((customer) => ({
            ...customer,
            messages: messages.filter((message) => message.sender_id === customer.user_id || message.receiver_id === customer.user_id)
        })).filter(c => c.messages.length > 0))
    }, [messages, customers])

    const fetchCustomers = () => {
        api.get('/customers').then(({ data }) => setCustomers(data)).catch(console.error)
    }

    const fetchMessages = () => {
        if (user) api.get('/livechat/messages').then(({ data }) => setMessages(data)).catch(console.error)
    }

    const markAsRead = () => {
        const customer = customersMessages?.[index]
        if (!customer) return;
        api.patch('/livechat/messages/messageRead', { sender_id: customer.user_id }).catch(console.error)
    }

    const scrollToBottom = () => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }

    const sendMessage = () => {
        const customer = customersMessages?.[index]
        if (!customer) return;
        setApiCalling(true)
        api.post('/livechat/messages', {
            message: message,
            receiver_id: customer.user_id
        }).then(() => {
            setMessage('')
        }).catch(console.error).finally(() => setApiCalling(false))
    }

    function idToColor(id: number) {
        // Predefined set of colors
        const colors = ['#D70040', '#811331', '#F88379', '#C41E3A', '#770737', '#4A0404'];

        // Calculate an index based on the id
        const index = Math.abs(id) % colors.length;

        // Return the corresponding color
        return colors[index];
    }

    if (!customersMessages) return <Container padding={4}><LiveChatSkeleton /></Container>
    if (customersMessages.length === 0) return <Container padding={2}><Typography>No messages from customers</Typography></Container>

    return (
        <Container padding={4} display={'flex'} alignItems={'center'}>
            <Grid2 container size={'grow'} display={'flex'} gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                <Container container size={'auto'} flexDirection={{ xs: 'row', md: 'column' }} justifyContent={'center'} alignItems={'start'} gap={1} height={'100%'} maxWidth={'70vw'} maxHeight={'75vh'} overflow={'auto'} wrap="nowrap" sx={{ border: 'lightgrey solid 1px', borderRadius: 4, padding: 2 }}>
                    {customersMessages
                        .map((cm, i) =>
                            <Grid2 key={i} display={'flex'} justifyContent={'center'} sx={{flexShrink: 0}}>
                                <Badge badgeContent={cm.messages.filter(m => m.receiver_id === null && m.read_by_receiver === false).length} color="primary">
                                    <Avatar sx={{ bgcolor: idToColor(cm.user_id), cursor: 'pointer' }} onClick={() => handleClick(i)}>{cm.name[0].toUpperCase()}</Avatar>
                                </Badge>
                            </Grid2>
                        )
                    }
                </Container>
                <Grid2 container size={'grow'}>
                    <Container container flexDirection={'column'} sx={{ border: '1px solid lightgrey', borderRadius: 4, padding: 3 }}>
                        <Grid2 sx={{ borderBottom: '1px solid lightgrey', padding: 1 }}>
                            <Typography variant="h6">Chat with {customersMessages[index]?.name}</Typography>
                        </Grid2>
                        <Grid2 container display={'flex'} flexDirection={'column'} maxHeight={{ xs: '40vh', md: '45vh', lg: '65vh' }} wrap="nowrap" sx={{ overflowY: 'auto', overflowX: 'hidden' }} ref={containerRef}>
                            {customersMessages[index]?.messages.map((message, i) => {
                                const customer = customersMessages[index]
                                return (
                                    <Grid2 container key={i}>
                                        <Grid2 display={'flex'} justifyContent={message.sender_id === customer.user_id ? 'left' : 'right'} size={12}>
                                            <Typography
                                                sx={{
                                                    backgroundColor: message.sender_id === customer.user_id ? 'primary.main' : 'lightgrey',
                                                    color: message.sender_id === customer.user_id ? 'primary.contrastText' : undefined,
                                                    display: 'inline-block',
                                                    maxWidth: '90%',
                                                    padding: 1,
                                                    borderRadius: 6,
                                                    marginY: 0.5
                                                }}
                                            >
                                                {message.message}
                                            </Typography>
                                        </Grid2>
                                        <Grid2 display={message.sender_id == customersMessages[index]?.messages[i + 1]?.sender_id ? 'none' : 'flex'} justifyContent={message.sender_id === customer.user_id ? 'left' : 'right'} size={12}>
                                            <Typography style={{
                                                color: 'grey',
                                                fontSize: 12
                                            }}>{new Date(message.creation_timestamp).toLocaleString(lang === 'ar' ? 'ar-SD' : undefined)}</Typography>
                                        </Grid2>
                                    </Grid2>
                                )
                            }
                            )}
                        </Grid2>
                        <Grid2 container size={12} justifyContent={'space-between'} sx={{ direction: 'ltr' }}>
                            <Grid2 size={11.5}>
                                <TextField size='small' fullWidth value={message} onChange={(e) => setMessage(e.target.value)} disabled={apiCalling} onKeyUp={e => e.key === 'Enter' ? sendMessage() : undefined} />
                            </Grid2>
                            <Grid2 size={0.5} display={'flex'}>
                                <IconButton onClick={sendMessage} disabled={apiCalling}>
                                    {apiCalling ? <CircularProgress /> : <Send sx={{ color: 'primary.main' }} />}
                                </IconButton>
                            </Grid2>
                        </Grid2>
                    </Container>
                </Grid2>
            </Grid2 >
        </Container>
    )
}